import { useEffect, useRef, useState, type Dispatch, type SetStateAction } from 'react';
import type { useSentinelComposer } from '@/hooks/use-sentinel-workspace';
import {
  COMPOSER_STATUS,
  WORKSPACE_MODAL,
  type WorkspaceModal,
} from '@/hooks/use-sentinel-workspace';
import { ActionCard } from './action-card';
import { AiChatOverlay } from './ai-chat-overlay';
import { BlueprintCard } from './blueprint-card';
import {
  ActionRunningBar,
  AutomationStatusBar,
  BlueprintLoadingBar,
  DoneBar,
  ErrorBar,
  HandoffReasonCard,
  GeneratingCard,
  RefiningBlueprintBar,
  WaitingForCustomerBar,
} from './composer-bars';
import { ReplyEditor } from './reply-editor';
import { AutomatedResponseEvidence, ResponseCard } from './response-card';

export type PendingSend = { markdown: string; close: boolean; responseId: string | null; files: File[] };

type ComposerState = ReturnType<typeof useSentinelComposer>;

/** Matches the overlay's exit duration so it is unmounted only once the animation ends. */
const OVERLAY_EXIT_MS = 200;

type ComposerPanelProps = {
  composer: ComposerState['composer'];
  actions: ComposerState['actions'];
  aiChat: ComposerState['aiChat'];
  refineLimitHit: boolean;
  regenerateReady: boolean;
  blueprintLimitHit: boolean | null;
  /** The brand skips the blueprint, so its first stage is already the response. */
  blueprintsOff?: boolean;
  sendBusy: boolean;
  setSendBusy: Dispatch<SetStateAction<boolean>>;
  onSetModal: (modal: WorkspaceModal) => void;
  onSend: (markdown: string, close: boolean, responseId: string | null, files: File[]) => void;
  attachmentsEnabled: boolean;
};

export function ComposerPanel({
  composer,
  actions,
  aiChat,
  refineLimitHit,
  regenerateReady,
  blueprintLimitHit,
  blueprintsOff,
  sendBusy,
  setSendBusy,
  onSetModal,
  onSend,
  attachmentsEnabled,
}: ComposerPanelProps) {
  return (
    <>
      {composer.status === COMPOSER_STATUS.blueprintLoading &&
        (blueprintsOff ? (
          <GeneratingCard
            trace={composer.trace}
            stepsDone={composer.stepsDone}
            expanded={composer.expanded}
            onToggleExpanded={actions.toggleGenerationExpanded}
          />
        ) : (
          <BlueprintLoadingBar
            trace={composer.trace}
            stepsDone={composer.stepsDone}
            expanded={composer.expanded}
            onToggleExpanded={actions.toggleGenerationExpanded}
          />
        ))}
      {composer.status === COMPOSER_STATUS.blueprint && (
        <BlueprintCard
          blueprint={composer.blueprint}
          refineLimitHit={blueprintLimitHit}
          refineOpen={composer.refineOpen}
          onRefineOpenChange={actions.setRefineOpen}
          onRefineSubmit={actions.refineBlueprint}
          onReject={() => onSetModal(WORKSPACE_MODAL.rejectBlueprint)}
          onGenerate={actions.generate}
        />
      )}
      {composer.status === COMPOSER_STATUS.blueprintRefining && <RefiningBlueprintBar />}
      {composer.status === COMPOSER_STATUS.generating && (
        <GeneratingCard
          trace={composer.trace}
          stepsDone={composer.stepsDone}
          expanded={composer.expanded}
          onToggleExpanded={actions.toggleGenerationExpanded}
        />
      )}
      {composer.status === COMPOSER_STATUS.waitingForCustomer && <WaitingForCustomerBar />}
      {composer.status === COMPOSER_STATUS.generationError && (
        // The reason reads first and the bar's actions stay last, as the automation evidence does.
        <div className="flex flex-col gap-2">
          {composer.reason && <HandoffReasonCard reason={composer.reason} />}
          <ErrorBar
            message={composer.message}
            onReject={() =>
              onSetModal(composer.failedAction ? WORKSPACE_MODAL.rejectBlueprint : WORKSPACE_MODAL.rejectResponse)
            }
            onHandleManually={composer.manual ? actions.handleManually : undefined}
            onRetry={composer.retryable ? actions.retryFailedOperation : undefined}
          />
        </div>
      )}
      {composer.status === COMPOSER_STATUS.response && (
        <ResponseCard
          response={composer.active}
          refineDisabled={refineLimitHit}
          onReject={() => onSetModal(WORKSPACE_MODAL.rejectResponse)}
          onRefine={() => aiChat.setOpen(true)}
          onRegenerate={regenerateReady ? actions.regenerateResponse : undefined}
          onUse={() => actions.useResponse()}
        />
      )}
      {composer.status === COMPOSER_STATUS.automated &&
        (composer.delivery === 'blocked' || composer.delivery === 'failed' ? (
          // Automation wrote a reply and did not deliver it. The agent sees what it did, the reply,
          // and can send it; refining is not offered, because the server does not regenerate an
          // automation turn. `unknown` is not offered: on most helpdesks it is a delivered reply.
          <div className="flex flex-col gap-2">
            <AutomationStatusBar delivery={composer.delivery} detail={composer.message} />
            {/* Folded: the unsent reply below is what the agent acts on; the steps stay one click away. */}
            <AutomatedResponseEvidence response={composer.response} defaultOpen={false} />
            <ResponseCard
              response={composer.response}
              refineDisabled
              onReject={() => onSetModal(WORKSPACE_MODAL.rejectResponse)}
              // Never reached: the card hides the refine button while refining is disabled.
              onRefine={() => undefined}
              onUse={() => actions.useResponse()}
            />
          </div>
        ) : (
          <div className="flex flex-col gap-2">
            <AutomatedResponseEvidence response={composer.response} />
            <AutomationStatusBar delivery={composer.delivery} />
          </div>
        ))}
      {composer.status === COMPOSER_STATUS.action && (
        <ActionCard
          action={composer.action}
          armed={composer.armed}
          onArm={actions.armAction}
          onConfirm={actions.confirmAction}
          onReject={() => onSetModal(WORKSPACE_MODAL.rejectBlueprint)}
          onRejectAndDraft={actions.openRejectDraft}
        />
      )}
      {composer.status === COMPOSER_STATUS.actionRunning && <ActionRunningBar label={composer.label ?? composer.action.progressLabel} />}
      {composer.status === COMPOSER_STATUS.editing && (
        <ReplyEditor
          key={composer.sourceResponseId ?? 'draft'}
          draftMarkdown={composer.draftMarkdown}
          editorKey={composer.sourceResponseId ?? 'draft'}
          onBackToResponse={composer.sourceResponseId ? actions.returnToResponse : undefined}
          onViewAiChat={composer.sourceResponseId && !composer.automated ? () => aiChat.setOpen(true) : undefined}
          onSend={(markdown, close, files) => onSend(markdown, close, composer.sourceResponseId, files)}
          onAddNote={async (markdown) => {
            setSendBusy(true);
            try {
              return await actions.addNote(markdown);
            } finally {
              setSendBusy(false);
            }
          }}
          sending={sendBusy}
          attachmentsEnabled={attachmentsEnabled}
        />
      )}
      {composer.status === COMPOSER_STATUS.done && (
        <DoneBar
          label={composer.label}
          onGenerate={composer.canGenerate ? actions.generateAgain : undefined}
          generating={composer.generating}
        />
      )}
    </>
  );
}

type AiChatOverlayHostProps = {
  actions: ComposerState['actions'];
  aiChat: ComposerState['aiChat'];
  versions: ComposerState['versions'];
  refineLimitHit: boolean;
  refineReady: boolean;
};

/**
 * Renders the AI chat over the whole conversation pane. It lives beside the composer rather than
 * inside it because the composer floats — an overlay nested in it would be clipped to that strip.
 */
export function AiChatOverlayHost({
  actions,
  aiChat,
  versions,
  refineLimitHit,
  refineReady,
}: AiChatOverlayHostProps) {
  // Kept mounted for the exit animation, so closing and "Use Response" both fade out.
  const [overlayMounted, setOverlayMounted] = useState(aiChat.open);
  useEffect(() => {
    if (aiChat.open) {
      setOverlayMounted(true);
      return;
    }
    const timer = setTimeout(() => setOverlayMounted(false), OVERLAY_EXIT_MS);
    return () => clearTimeout(timer);
  }, [aiChat.open]);

  // A replan closes the chat and drops the running refine in the same commit, which would empty
  // the card mid-exit. Hold the last open view so it animates out as the agent last saw it.
  const shown = useRef({ versions, refineLabels: aiChat.refineLabels, pendingRefine: aiChat.pendingRefine });
  if (aiChat.open) {
    shown.current = { versions, refineLabels: aiChat.refineLabels, pendingRefine: aiChat.pendingRefine };
  }

  if (!overlayMounted) return null;

  const rejecting = aiChat.rejectDraftAction;
  if (rejecting) {
    return (
      <AiChatOverlay
        closing={!aiChat.open}
        onClose={actions.closeRejectDraft}
        instruction={{
          placeholder: 'Describe what Kim AI should do instead...',
          onSubmit: (notes) => actions.rejectActionWithDraft(notes),
          // The action stays on screen so the agent can read what they are turning down.
          context: (
            <div className="flex w-full flex-col gap-1.5 rounded-2xl border border-accent-purple/50 bg-white/80 p-4">
              <span className="font-mono text-[12px] font-medium uppercase leading-4 text-text-disabled">
                {rejecting.label}
              </span>
              <p className="text-[13px] leading-[18px] text-text-body">{rejecting.description}</p>
            </div>
          ),
        }}
      />
    );
  }

  return (
    <AiChatOverlay
      closing={!aiChat.open}
      versions={shown.current.versions}
      refineLabels={shown.current.refineLabels}
      pendingRefine={shown.current.pendingRefine}
      refineError={aiChat.refineError}
      refineLimitHit={refineLimitHit}
      refineReady={refineReady}
      onClose={() => aiChat.setOpen(false)}
      onUse={(responseId) => actions.useResponse(responseId)}
      onRefineStyle={(style, label) => actions.refineResponse({ command: style }, `Refine : ${label.toLowerCase()}`)}
      onFreeRefine={(input, label) => actions.refineResponse(input, label)}
      onRetryRefine={actions.retryRefine}
    />
  );
}
